import { COLOR_PALETTE, STROKE_WIDTHS } from './types';
import type { ActiveTool } from './types';
import { PenIcon, HandIcon, EraserIcon } from './icons';
import { Button } from '@safetyhub/ui';

interface AnnotationToolPanelProps {
  layout: 'side' | 'bottom';
  showAnnotation: boolean;
  setShowAnnotation: (v: boolean) => void;
  activeTool: ActiveTool;
  setActiveTool: (t: ActiveTool) => void;
  isEraserMode: boolean;
  currentColor: string;
  setCurrentColor: (c: string) => void;
  currentWidth: number;
  setCurrentWidth: (w: number) => void;
  customColor: string;
  setCustomColor: (c: string) => void;
  strokeCount: number;
  onClearAll: () => void;
}

export function AnnotationToolPanel({
  layout,
  showAnnotation,
  setShowAnnotation,
  activeTool,
  setActiveTool,
  isEraserMode,
  currentColor,
  setCurrentColor,
  currentWidth,
  setCurrentWidth,
  customColor,
  setCustomColor,
  strokeCount,
  onClearAll,
}: AnnotationToolPanelProps) {
  if (layout === 'side') {
    return <SidePanel
      showAnnotation={showAnnotation}
      setShowAnnotation={setShowAnnotation}
      activeTool={activeTool}
      setActiveTool={setActiveTool}
      isEraserMode={isEraserMode}
      currentColor={currentColor}
      setCurrentColor={setCurrentColor}
      currentWidth={currentWidth}
      setCurrentWidth={setCurrentWidth}
      customColor={customColor}
      setCustomColor={setCustomColor}
      strokeCount={strokeCount}
      onClearAll={onClearAll}
    />;
  }

  return <BottomPanel
    showAnnotation={showAnnotation}
    setShowAnnotation={setShowAnnotation}
    activeTool={activeTool}
    setActiveTool={setActiveTool}
    isEraserMode={isEraserMode}
    currentColor={currentColor}
    setCurrentColor={setCurrentColor}
    currentWidth={currentWidth}
    setCurrentWidth={setCurrentWidth}
    customColor={customColor}
    setCustomColor={setCustomColor}
    strokeCount={strokeCount}
    onClearAll={onClearAll}
  />;
}

type PanelContentProps = Omit<AnnotationToolPanelProps, 'layout'>;

function SidePanel(props: PanelContentProps) {
  const {
    showAnnotation, setShowAnnotation,
    activeTool, setActiveTool, isEraserMode,
    currentColor, setCurrentColor,
    currentWidth, setCurrentWidth,
    customColor, setCustomColor,
    strokeCount, onClearAll,
  } = props;

  return (
    <div className="w-64 bg-ink-900 p-4 flex flex-col gap-4 overflow-y-auto z-30 relative">
      {/* 메모 보기 토글 */}
      <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={showAnnotation}
          onChange={(e) => setShowAnnotation(e.target.checked)}
          className="w-4 h-4 rounded"
        />
        메모 보기
      </label>

      {showAnnotation && (
        <>
          {/* 색상 선택 */}
          <div className="space-y-2">
            <div className="text-white text-sm font-medium">색상</div>
            <div className="flex flex-wrap gap-2">
              {COLOR_PALETTE.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => {
                    setCurrentColor(color.value);
                    setActiveTool('draw');
                  }}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    currentColor === color.value && !isEraserMode
                      ? 'border-white scale-110'
                      : 'border-ink-600 hover:border-ink-400'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
              <CustomColorButton
                customColor={customColor}
                setCustomColor={setCustomColor}
                setCurrentColor={setCurrentColor}
                setActiveTool={setActiveTool}
                currentColor={currentColor}
                isEraserMode={isEraserMode}
                size="w-8 h-8"
              />
            </div>
          </div>

          {/* 굵기 선택 */}
          <div className="space-y-2">
            <div className="text-white text-sm font-medium">굵기</div>
            <div className="flex items-center gap-2">
              {STROKE_WIDTHS.map((width) => (
                <button
                  key={width}
                  type="button"
                  onClick={() => setCurrentWidth(width)}
                  className={`flex items-center justify-center w-8 h-8 rounded transition-all ${
                    currentWidth === width
                      ? 'bg-white/20 ring-2 ring-white'
                      : 'hover:bg-white/10'
                  }`}
                  title={`${width}px`}
                >
                  <div className="rounded-full bg-white" style={{ width, height: width }} />
                </button>
              ))}
            </div>
          </div>

          {/* 도구 */}
          <div className="space-y-2">
            <div className="text-white text-sm font-medium">도구</div>
            <ToolButtons activeTool={activeTool} setActiveTool={setActiveTool} isEraserMode={isEraserMode} size="large" />
            <Button
              type="button"
              variant="danger"
              className="w-full"
              onClick={onClearAll}
              disabled={strokeCount === 0}
            >
              모두 지우기
            </Button>
          </div>

          {/* 현재 선택 미리보기 */}
          <div className="mt-4 p-3 bg-ink-800 rounded-card">
            <div className="text-ink-400 text-xs mb-2">미리보기</div>
            <div className="flex items-center gap-3">
              <div
                className="w-16 h-0 rounded-full"
                style={{
                  borderTopWidth: currentWidth,
                  borderTopColor: isEraserMode ? '#888' : currentColor,
                  borderTopStyle: isEraserMode ? 'dashed' : 'solid',
                }}
              />
              <span className="text-white text-xs">
                {isEraserMode ? '지우개' : `${currentWidth}px`}
              </span>
            </div>
          </div>

          {/* 조작 안내 */}
          <div className="mt-4 p-3 bg-ink-800 rounded-card text-ink-400 text-xs space-y-1">
            <div className="font-medium text-ink-300 mb-2">조작 안내</div>
            <div>• 스크롤: 확대/축소</div>
            <div>• 두 손가락 드래그 / Alt+드래그: 이동</div>
            <div>• 그리기 도구: 드래그로 메모</div>
            <div>• 이동 도구: 드래그로 사진 이동</div>
          </div>
        </>
      )}
    </div>
  );
}

function BottomPanel(props: PanelContentProps) {
  const {
    showAnnotation, setShowAnnotation,
    activeTool, setActiveTool, isEraserMode,
    currentColor, setCurrentColor,
    currentWidth, setCurrentWidth,
    customColor, setCustomColor,
    strokeCount, onClearAll,
  } = props;

  return (
    <div className="bg-ink-900 p-3 z-30">
      {/* 상단: 메모 보기 토글 + 도구 버튼 */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <label className="flex items-center gap-2 text-white text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={showAnnotation}
            onChange={(e) => setShowAnnotation(e.target.checked)}
            className="w-4 h-4 rounded"
          />
          메모
        </label>

        {showAnnotation && (
          <div className="flex items-center gap-2">
            <ToolButtons activeTool={activeTool} setActiveTool={setActiveTool} isEraserMode={isEraserMode} size="small" />
            <Button
              type="button"
              variant="danger"
              size="xs"
              onClick={onClearAll}
              disabled={strokeCount === 0}
            >
              전체삭제
            </Button>
          </div>
        )}
      </div>

      {showAnnotation && (
        <div className="flex items-center gap-4">
          {/* 색상 선택 */}
          <div className="flex items-center gap-1.5">
            {COLOR_PALETTE.map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => {
                  setCurrentColor(color.value);
                  setActiveTool('draw');
                }}
                className={`w-6 h-6 rounded-full border-2 transition-all ${
                  currentColor === color.value && !isEraserMode
                    ? 'border-white scale-110'
                    : 'border-ink-600'
                }`}
                style={{ backgroundColor: color.value }}
              />
            ))}
            <CustomColorButton
              customColor={customColor}
              setCustomColor={setCustomColor}
              setCurrentColor={setCurrentColor}
              setActiveTool={setActiveTool}
              currentColor={currentColor}
              isEraserMode={isEraserMode}
              size="w-6 h-6"
            />
          </div>

          <div className="w-px h-6 bg-ink-700" />

          {/* 굵기 선택 */}
          <div className="flex items-center gap-1">
            {STROKE_WIDTHS.map((width) => (
              <button
                key={width}
                type="button"
                onClick={() => setCurrentWidth(width)}
                className={`flex items-center justify-center w-6 h-6 rounded transition-all ${
                  currentWidth === width
                    ? 'bg-white/20 ring-1 ring-white'
                    : ''
                }`}
              >
                <div
                  className="rounded-full bg-white"
                  style={{ width: Math.max(width * 0.7, 2), height: Math.max(width * 0.7, 2) }}
                />
              </button>
            ))}
          </div>

          {/* 현재 선택 미리보기 */}
          <div className="ml-auto flex items-center gap-2">
            <div
              className="w-8 h-0 rounded-full"
              style={{
                borderTopWidth: currentWidth,
                borderTopColor: isEraserMode ? '#888' : currentColor,
                borderTopStyle: isEraserMode ? 'dashed' : 'solid',
              }}
            />
            <span className="text-white text-xs">
              {currentWidth}px
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// 도구 버튼 (그리기/이동/지우개)
function ToolButtons({ activeTool, setActiveTool, isEraserMode, size }: {
  activeTool: ActiveTool;
  setActiveTool: (t: ActiveTool) => void;
  isEraserMode: boolean;
  size: 'large' | 'small';
}) {
  const isPanTool = activeTool === 'pan';

  if (size === 'large') {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setActiveTool('draw')}
          className={`flex-1 py-2 rounded transition-colors flex items-center justify-center ${
            activeTool === 'draw' ? 'bg-white text-black' : 'bg-ink-700 text-white hover:bg-ink-600'
          }`}
          title="그리기"
        >
          <PenIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => setActiveTool('pan')}
          className={`flex-1 py-2 rounded transition-colors flex items-center justify-center ${
            isPanTool ? 'bg-white text-black' : 'bg-ink-700 text-white hover:bg-ink-600'
          }`}
          title="이동"
        >
          <HandIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => setActiveTool(isEraserMode ? 'draw' : 'eraser')}
          className={`flex-1 py-2 rounded transition-colors flex items-center justify-center ${
            isEraserMode ? 'bg-white text-black' : 'bg-ink-700 text-white hover:bg-ink-600'
          }`}
          title="지우개"
        >
          <EraserIcon className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setActiveTool('draw')}
        className={`px-2 py-1.5 rounded text-xs transition-colors ${
          activeTool === 'draw' ? 'bg-white text-black' : 'bg-ink-700 text-white'
        }`}
        title="그리기"
      >
        <PenIcon className="w-4 h-4 inline-block" />
      </button>
      <button
        type="button"
        onClick={() => setActiveTool('pan')}
        className={`px-2 py-1.5 rounded text-xs transition-colors ${
          isPanTool ? 'bg-white text-black' : 'bg-ink-700 text-white'
        }`}
        title="이동"
      >
        <HandIcon className="w-4 h-4 inline-block" />
      </button>
      <button
        type="button"
        onClick={() => setActiveTool(isEraserMode ? 'draw' : 'eraser')}
        className={`px-2 py-1.5 rounded text-xs transition-colors ${
          isEraserMode ? 'bg-white text-black' : 'bg-ink-700 text-white'
        }`}
        title="지우개"
      >
        <EraserIcon className="w-4 h-4 inline-block" />
      </button>
    </>
  );
}

// 커스텀 색상 피커
function CustomColorButton({ customColor, setCustomColor, setCurrentColor, setActiveTool, currentColor, isEraserMode, size }: {
  customColor: string;
  setCustomColor: (c: string) => void;
  setCurrentColor: (c: string) => void;
  setActiveTool: (t: ActiveTool) => void;
  currentColor: string;
  isEraserMode: boolean;
  size: string;
}) {
  const isCustom = !COLOR_PALETTE.some(c => c.value === currentColor) && !isEraserMode;

  return (
    <div className="relative">
      <input
        type="color"
        value={customColor}
        onChange={(e) => {
          setCustomColor(e.target.value);
          setCurrentColor(e.target.value);
          setActiveTool('draw');
        }}
        className={`absolute inset-0 ${size} opacity-0 cursor-pointer`}
      />
      <div
        className={`${size} rounded-full border-2 transition-all ${
          isCustom ? 'border-white scale-110' : 'border-ink-600 hover:border-ink-400'
        }`}
        style={{ background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)' }}
        title="사용자 색상"
      />
    </div>
  );
}
